import { gql, Resolver } from '@apollo/client';
import ImageBlobReducer from 'image-blob-reduce';

/**
 * @typedef {TaskResizeImageArgs}
 * @property {DoTaskUpdates} doTaskUpdates map of task updates to send with context.sendTaskEvent
 * @property {File} file the image file to be resized
 * @property {number} maxSize the maximum dimension to be allowed for the resized file. Will be
 * applied to either height or width, whichever is larger, and then the other dimension will be
 * scaled proportionally.
 * @property {number} progressShare the share of the overall progress percentage that this portion
 * of the task will be able to complete; i.e. 100 for 100% if taskResizeImage is the only step in
 * the task.
 * @property {string} taskId ID for the relevant task in the TaskManagerMachine
 */
interface TaskResizeImageArgs {
    doTaskUpdates?: DoTaskUpdates;
    file: File;
    maxSize: number;
    progressOffset?: number;
    progressShare?: number;
    taskId: string;
}

/**
 * @typedef {TaskResizeImageContext}
 * @property {function} sendTaskEvent an action dispatcher for the TaskManagementMachine
 */
interface TaskResizeImageContext {
    sendTaskEvent: SendTaskEvent;
}

/**
 * Resize an image, given a File instance for to the image and a maxSize.
 * @param obj the parent object
 * @param args {TaskResizeImageArgs}
 * @param context {TaskResizeImageContext}
 */
export const taskResizeImage: Resolver = async (
    obj: Record<string, unknown>,
    args: TaskResizeImageArgs,
    context: TaskResizeImageContext,
): Promise<ResizeImageOutput> => {
    const {
        doTaskUpdates = {
            error: true,
            success: false,
            progress: true,
        },
        file,
        maxSize,
        taskId,
        progressOffset = 0,
        progressShare = 100,
    } = args;
    const { sendTaskEvent } = context;

    const reducer = ImageBlobReducer();

    try {
        const blob = await reducer.toBlob(file, { max: maxSize });

        return {
            blob,
        };
    } catch (error) {
        if (doTaskUpdates.error) {
            sendTaskEvent({
                type: 'ERROR',
                value: {
                    taskId,
                    error: `Failed to resize image: ${file.name}`,
                },
            });
        }
    }

    if (doTaskUpdates.progress) {
        sendTaskEvent({
            type: 'PROGRESS',
            value: {
                taskId,
                progress: progressOffset + progressShare,
            },
        });
    }

    return {
        blob: null,
    };
};

export const TASK_RESIZE_IMAGE = gql`
    mutation TaskResizeImage($file: String!, $maxSize: Int!) {
        taskResizeImage(file: $file, maxSize: $maxSize) @client
    }
`;
